import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal from 'components/shared/StandardModal';
import { AgentSettings, MappingType } from 'services/api/admin/mappings';
import { colors } from 'styles/theme/colors';

import { Muted } from './Chips';
import { AgentInstructions, AgentModeSettings, NewHospitalSettings } from './MappingSettings';
import { PendingOperation, TYPE_TITLE } from './operations';

type Props = {
  type: MappingType;
  ehr: boolean;
  agent: AgentSettings | undefined;
  instructions: string | null;
  settingsOp: PendingOperation | undefined;
  instructionsOp: PendingOperation | undefined;
  disabled: boolean;
  onSettingsChange: (patch: { threshold?: number | null; newHospitalThreshold?: number | null }) => void;
  onInstructionsChange: (text: string | null) => void;
  onUndo: (slot: string) => void;
  onClose: () => void;
};

export default function AgentSettingsModal({
  type,
  ehr,
  agent,
  instructions,
  settingsOp,
  instructionsOp,
  disabled,
  onSettingsChange,
  onInstructionsChange,
  onUndo,
  onClose,
}: Props) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  const settings = settingsOp?.op === 'settings' ? settingsOp : null;
  const pendingThreshold =
    settings?.threshold !== undefined && settings.threshold !== agent?.threshold ? settings.threshold : null;
  const pendingNewHospital =
    settings?.newHospitalThreshold !== undefined && settings.newHospitalThreshold !== agent?.newHospitals?.threshold
      ? settings.newHospitalThreshold
      : null;

  return createPortal(
    <StandardModal title='Auto-mapping agent settings' subtitle={TYPE_TITLE[type]} onCancel={onClose} maxWidth='640px'>
      <Body>
        {ehr && agent && (
          <AgentModeSettings
            type={type}
            stored={agent}
            pending={pendingThreshold}
            disabled={disabled}
            onChange={(threshold) => onSettingsChange({ threshold })}
          />
        )}
        {ehr && agent?.newHospitals && (
          <NewHospitalSettings
            stored={agent.newHospitals}
            agentEnabled={agent.enabled}
            pending={pendingNewHospital}
            disabled={disabled}
            onChange={(newHospitalThreshold) => onSettingsChange({ newHospitalThreshold })}
          />
        )}
        <AgentInstructions
          title={TYPE_TITLE[type]}
          stored={instructions}
          pending={instructionsOp?.op === 'instructions' ? instructionsOp.text : null}
          problem={instructionsOp}
          disabled={disabled}
          onChange={onInstructionsChange}
          onUndo={onUndo}
        />
      </Body>
      <Footer>
        <Muted>Changes stay pending until you apply and submit.</Muted>
        <Button onClick={onClose}>Done</Button>
      </Footer>
    </StandardModal>,
    document.body
  );
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${colors.black10};
`;
